import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CursorArrowRaysIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { HtmlStepperFormData } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@superfunnel/ui-core/v2/Dialog';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  formFieldUrlWithParamsValidator,
  isValidUrlWithParams,
  UrlValidationConfig,
} from '@superfunnel/utils';
import { useCallback, useContext, useEffect, useState } from 'react';
import { SnackbarContext } from '../contexts/SnackbarContext';

const urlValidationConfig: UrlValidationConfig = {
  allowHashOnly: true,
  allowRelative: true,
  allowMailto: true,
  allowTel: true,
};

/**
 * Stepper action types:
 * - next: Navigate to next step (submits on last step)
 * - back: Navigate to previous step
 * - goto: Navigate to a specific step by ID
 * - link: External URL link
 * - none: No action (disabled button)
 */
export type StepperActionType = 'next' | 'back' | 'goto' | 'link' | 'none';

export interface StepperActionData {
  actionType: StepperActionType;
  /** Target step ID for 'goto' action */
  gotoStepId?: string;
  /** URL for 'link' action */
  url?: string;
  /** Open link in new tab */
  openInNewTab?: boolean;
}

interface Props {
  data?: StepperActionData;
  formData: HtmlStepperFormData | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: StepperActionData) => Promise<void> | void;
}

const createDefaultStepperAction = (): StepperActionData => ({
  actionType: 'next',
  gotoStepId: '',
  url: '',
  openInNewTab: false,
});

/**
 * Parse existing HTML action attributes into StepperActionData.
 * Handles the action/url/openInNewTab structure.
 */
export function parseHtmlActionToStepperAction(attrs: {
  action: string;
  url?: string;
  openInNewTab?: boolean;
}): StepperActionData {
  const { action, url, openInNewTab } = attrs;

  // Handle navigation actions
  if (action === 'next' || action === 'back') {
    return { actionType: action };
  }

  // Handle goto action (e.g., "goto:stepId")
  if (action.startsWith('goto:')) {
    return {
      actionType: 'goto',
      gotoStepId: action.replace('goto:', ''),
    };
  }

  // Handle link action
  if (action === 'link' && url) {
    return {
      actionType: 'link',
      url: url,
      openInNewTab: openInNewTab ?? false,
    };
  }

  // Default to none if no recognizable action
  return { actionType: 'none' };
}

/**
 * Convert StepperActionData back to HTML attributes for the update.
 * Returns the action/url/openInNewTab structure.
 */
export function stepperActionToHtmlAttributes(data: StepperActionData): {
  action: string;
  url?: string;
  openInNewTab?: boolean;
} {
  switch (data.actionType) {
    case 'next':
    case 'back':
      return { action: data.actionType };

    case 'goto':
      return {
        action: data.gotoStepId ? `goto:${data.gotoStepId}` : 'none',
      };

    case 'link':
      return {
        action: 'link',
        url: data.url || undefined,
        openInNewTab: data.openInNewTab ?? false,
      };

    case 'none':
    default:
      return { action: 'none' };
  }
}

export default function EditStepperActionModal({
  data: initialData,
  formData,
  open,
  onClose,
  onSave,
}: Props) {
  const { show } = useContext(SnackbarContext);

  const [data, setData] = useState<StepperActionData>(() =>
    createDefaultStepperAction(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();

  // Get available steps for the goto dropdown
  const availableSteps = formData?.steps ?? [];
  const availableFinalSteps = formData?.finalSteps ?? [];

  // Reset form data when modal opens
  useEffect(() => {
    if (!open) return;

    const newData = initialData
      ? { ...initialData }
      : createDefaultStepperAction();

    if (newData.openInNewTab === undefined) {
      newData.openInNewTab = false;
    }

    setData(newData);
    setError(undefined);
    setIsSaving(false);
  }, [open, initialData]);

  const saveAction = useCallback(async () => {
    setError(undefined);

    // Validate goto action has a step selected
    if (data.actionType === 'goto' && !data.gotoStepId) {
      setError(new Error('Please select a step to navigate to'));
      show('Please select a step to navigate to');
      return;
    }

    // Validate link action has a URL
    if (data.actionType === 'link' && !data.url) {
      setError(new Error('Link URL is required'));
      show('Link URL is required');
      return;
    }

    // Validate URL format
    if (
      data.actionType === 'link' &&
      data.url &&
      !isValidUrlWithParams(data.url, urlValidationConfig)
    ) {
      setError(new Error('Link URL is not valid'));
      show('Link URL is not valid');
      return;
    }

    const res = onSave(data);
    if (res && 'then' in res) {
      setIsSaving(true);
      res
        .catch((e) => {
          setError(e);
          Sentry.captureException(e);
        })
        .finally(() => setIsSaving(false));
    }
  }, [data, onSave, show]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Button Action</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Action Type Selector */}
          <div className="relative flex flex-col justify-start gap-1">
            <FieldTitle title="Action Type" />
            <Select
              value={data.actionType}
              onValueChange={(actionType: StepperActionType) => {
                setData((d) => ({
                  ...d,
                  actionType,
                  // Reset related fields when changing action type
                  gotoStepId: actionType === 'goto' ? d.gotoStepId : '',
                  url: actionType === 'link' ? d.url : '',
                }));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select action type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="next">
                  <span className="flex items-center gap-2">
                    <ArrowRightIcon className="h-4 w-4" />
                    Next Step
                  </span>
                </SelectItem>
                <SelectItem value="back">
                  <span className="flex items-center gap-2">
                    <ArrowLeftIcon className="h-4 w-4" />
                    Previous Step
                  </span>
                </SelectItem>
                <SelectItem value="goto">
                  <span className="flex items-center gap-2">
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    Go to Step
                  </span>
                </SelectItem>
                <SelectItem value="link">
                  <span className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Link
                  </span>
                </SelectItem>
                <SelectItem value="none">
                  <span className="flex items-center gap-2">
                    <CursorArrowRaysIcon className="h-4 w-4" />
                    None
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Go to Step Selector */}
          {data.actionType === 'goto' && (
            <div className="flex flex-col justify-start gap-1">
              <FieldTitle title="Target Step" />
              <Select
                value={data.gotoStepId || ''}
                onValueChange={(gotoStepId: string) => {
                  setData((d) => ({
                    ...d,
                    gotoStepId,
                  }));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a step" />
                </SelectTrigger>
                <SelectContent>
                  {availableSteps.map((step, index) => (
                    <SelectItem key={step.id} value={step.id}>
                      {step.name || `Step ${index + 1}`}
                    </SelectItem>
                  ))}
                  {availableFinalSteps.map((finalStep) => (
                    <SelectItem key={finalStep.id} value={finalStep.id}>
                      {finalStep.name || 'Final Step'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Link URL Input */}
          {data.actionType === 'link' && (
            <>
              <Input
                title="Link URL"
                value={data.url || ''}
                type="url"
                placeholder="https://example.com"
                onChange={(e) => {
                  setData((d) => ({
                    ...d,
                    url: e.target.value,
                  }));
                }}
                validation={
                  data.url
                    ? formFieldUrlWithParamsValidator(
                        data.url,
                        true,
                        urlValidationConfig,
                      )
                    : undefined
                }
              />

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Open in New Tab
                  </label>
                  <p className="text-sm text-gray-500">
                    Open the link in a new browser tab
                  </p>
                </div>
                <Switch
                  checked={data.openInNewTab}
                  onCheckedChange={(checked: boolean) => {
                    setData((d) => ({
                      ...d,
                      openInNewTab: checked,
                    }));
                  }}
                />
              </div>
            </>
          )}

          {/* Error Display */}
          {error && (
            <div className="w-full text-end text-red-600">
              {error.message || 'Failed to save'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={saveAction} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
